import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from './supabase';
import { vendorOf, VENDORS } from './constants';

export default function ShareView() {
  const { shareId } = useParams();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const load = async () => {
      const { data: listData, error: listErr } = await supabase
        .from('lists').select('*').eq('share_id', shareId).single();

      if (listErr || !listData) { setNotFound(true); setLoading(false); return; }
      setList(listData);
      console.log('list data:', listData);
      console.log('notes value:', listData.notes);

      const { data: itemData } = await supabase
        .from('items').select('*').eq('list_id', listData.id).order('created_at', { ascending: true });

      const { data: attData } = await supabase
        .from('list_attachments').select('*').eq('list_id', listData.id).order('created_at', { ascending: true });

      setItems(itemData || []);
      setAttachments(attData || []);
      setLoading(false);
    };
    load();
  }, [shareId]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
      <div className="spin" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: 'white', borderRadius: '50%' }} />
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', gap: 12 }}>
      <p style={{ color: 'var(--text-secondary)', fontSize: 16, fontFamily: 'DM Sans, sans-serif' }}>List not found or link has expired.</p>
    </div>
  );

  const groups = VENDORS.map(v => ({
    vendor: v.id,
    items: items.filter(i => i.vendor === v.id),
  })).filter(g => g.items.length > 0);

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.unitPrice)||0) * (parseInt(i.qty)||1), 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 20px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--action-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0e1015" strokeWidth="2.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{list.name}</h1>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 42 }}>
            {items.length} item{items.length !== 1 ? 's' : ''} · {groups.length} vendor{groups.length !== 1 ? 's' : ''}
            {subtotal > 0 && <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}> · ${subtotal.toFixed(2)}</span>}
          </p>
          <div style={{
            marginTop: 14, paddingLeft: 42,
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: 'var(--text-faint)'
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            View-only link — tap "Open all" to shop each vendor
          </div>

          {/* Floor plan */}
          {list.floor_plan_url && (
            <div style={{ marginTop: 14, paddingLeft: 42 }}>
              <a href={list.floor_plan_url} target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 10, textDecoration: 'none',
                background: 'var(--bg-input)', border: '1px solid var(--border-mid)',
                color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 500,
                transition: 'all 0.15s'
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                View Floor Plan — {list.floor_plan_name}
              </a>
            </div>
          )}
        </div>

        {/* Groups */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {groups.map(g => {
            const v = vendorOf(g.vendor);
            const groupTotal = g.items.reduce((s, i) => s + (parseFloat(i.unitPrice)||0) * (parseInt(i.qty)||1), 0);
            const withLinks = g.items.filter(i => i.url);

            const openAll = () => withLinks.forEach((item, idx) =>
              setTimeout(() => window.open(item.url, '_blank'), idx * 350)
            );

            return (
              <div key={g.vendor} style={{ borderRadius: 18, overflow: 'hidden', border: `1px solid ${v.border}` }}>
                {/* Group header */}
                <div style={{ background: v.bg, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: v.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{v.icon}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: v.color }}>{v.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {g.items.length} item{g.items.length !== 1 ? 's' : ''}
                        {groupTotal > 0 && ` · $${groupTotal.toFixed(2)}`}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {g.items.length} item{g.items.length !== 1 ? 's' : ''}
                    {groupTotal > 0 && ` · $${groupTotal.toFixed(2)}`}
                  </span>
                </div>

                {/* Items */}
                <div style={{ background: 'var(--bg-secondary)' }}>
                  {g.items.map((item, idx) => {
                    const lineTotal = item.unitPrice
                      ? (parseFloat(item.unitPrice) * (parseInt(item.qty)||1)).toFixed(2)
                      : null;

                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '13px 16px',
                        borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{item.name}</span>
                            {item.sku && <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-faint)' }}>{item.sku}</span>}
                          </div>
                          {item.notes && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{item.notes}</p>}
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
                              fontSize: 11, color: v.color, opacity: 0.55,
                              display: 'block', marginTop: 3, textDecoration: 'none',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}
                            onMouseOver={e => e.target.style.opacity = 1}
                            onMouseOut={e => e.target.style.opacity = 0.55}
                            >{item.url.replace(/^https?:\/\//, '').slice(0, 50)}{item.url.length > 55 ? '…' : ''}</a>
                          )}
                        </div>

                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Qty</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{item.qty}</div>
                        </div>

                        {lineTotal && (
                          <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 60 }}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total</div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>${lineTotal}</div>
                          </div>
                        )}
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
                            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
                            padding: '7px 14px', borderRadius: 10, textDecoration: 'none',
                            background: v.color, color: 'var(--text-primary)', fontSize: 12, fontWeight: 700
                          }}>
                            Open
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Notes */}
          {list.notes && list.notes.trim() !== '' && (
            <div style={{ marginTop: 8, padding: '20px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 10 }}>📋 Notes from your installer</div>
              <p style={{ fontSize: 15, color: '#ffffff', lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0 }}>{list.notes}</p>
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 10 }}>
                Attachments ({attachments.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                {attachments.map(att => {
                  const isImg = att.type?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name);
                  return (
                    <div key={att.id} onClick={() => setPreview(att)} style={{
                      aspectRatio: '1', borderRadius: 10, overflow: 'hidden',
                      background: 'var(--bg-card)', border: '1px solid var(--border)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {isImg ? (
                        <img src={att.url} alt={att.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: 8 }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <span style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{att.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Preview modal */}
          {preview && (
            <div onClick={() => setPreview(null)} style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 200, padding: 20
            }}>
              <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', position: 'relative' }}>
                {preview.type?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(preview.name) ? (
                  <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain' }} />
                ) : (
                  <div style={{ background: 'var(--bg-modal)', borderRadius: 16, padding: 32, textAlign: 'center' }}>
                    <p style={{ color: 'var(--text-primary)', marginBottom: 16 }}>{preview.name}</p>
                    <a href={preview.url} target="_blank" rel="noopener noreferrer" style={{
                      padding: '10px 20px', borderRadius: 10, background: 'var(--action-bg)',
                      color: 'var(--action-text)', fontWeight: 700, textDecoration: 'none', fontSize: 13
                    }}>Open File</a>
                  </div>
                )}
                <button onClick={() => setPreview(null)} style={{
                  position: 'absolute', top: -12, right: -12,
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)', border: 'none',
                  color: 'var(--text-primary)', cursor: 'pointer', fontSize: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>×</button>
              </div>
            </div>
          )}

          {/* Grand total */}
          {subtotal > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '16px 20px', minWidth: 200 }}>
                {groups.map(g => {
                  const v = vendorOf(g.vendor);
                  const t = g.items.reduce((s,i) => s+(parseFloat(i.unitPrice)||0)*(parseInt(i.qty)||1),0);
                  if (!t) return null;
                  return (
                    <div key={g.vendor} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                      <span style={{ color: v.color, opacity: 0.8 }}>{v.name}</span>
                      <span style={{ color: 'var(--text-label)' }}>${t.toFixed(2)}</span>
                    </div>
                  );
                })}
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  <span>Total</span><span>${subtotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
